<template>
  <div>
    <div class="namespace-team-page" v-if="!isLoading">
      <TirPmModal
        :is-visible="isVisible"
        title="Доступ к тикетам всех очередей будет аннулирован"
        :is-with-back="false"
        @close="isVisible = false"
      >
        <template #content>
          Тикеты, наблюдаемые и назначенные в работу, станут недоступны
        </template>
        <template #footer>
          <TirPmButton
            :size="TirPmButtonSizeEnum.Regular"
            :variant="TirPmButtonVariantEnum.PrimaryState"
            @click="removeTeamForNamespace(teamUserItem)"
          >
            <div style="padding: 0 0.5rem">Продолжить</div>
          </TirPmButton>
          <TirPmButton
            :size="TirPmButtonSizeEnum.Regular"
            :variant="TirPmButtonVariantEnum.LinkState"
            @click="isVisible = false"
          >
            <div style="padding: 0 0.688rem">Отменить</div>
          </TirPmButton>
        </template>
      </TirPmModal>

      <h2 class="namespace-team-page__title">Команда</h2>
      <div class="namespace-team-page__content">
        <TirPmSearchInput
          style="width: 100%"
          label="Команда"
          placeholder=""
          :isWithPopup="true"
          v-model="searchQueryUsers"
          @input="filteredUser"
          :isDisabled="!isOwner && !isAdminProject && !isAdminNamespace"
        >
          <template #content>
            <TirPmSearchInputContentLine
              v-for="user of copiComparedUserList"
              :is-disabled="user.IsProjectAdmin || user.IsNamespaceAdmin"
              :key="user.Id"
              @mousedown="onMouseDown(user, $event)"
              @click.stop="setTeamToNamespace(user)"
            >
              <template #leftSlot>
                <TirPmIconView
                  :size="24"
                  isRounded
                  :isBordered="false"
                  :colorCode="user.AvatarColor"
                  :imageLoadUrl="user.Photo"
                >
                  <template #mainSlot>
                    {{ user?.FirstName?.[0].toUpperCase() }}{{ user?.LastName?.[0].toUpperCase() }}
                  </template>
                </TirPmIconView>
              </template>

              <span>{{ user.FirstName }} {{ user.MiddleName }} {{ user.LastName }}</span>
              <span v-if="user.IsProjectAdmin"> (Администратор проекта)</span>
              <span v-else-if="user.IsNamespaceAdmin"> (Администратор раб. области)</span>

              <template #rightSlot></template>
            </TirPmSearchInputContentLine>
          </template>
        </TirPmSearchInput>
      </div>
      <div class="namespace-team-page__users-list">
        <div class="namespace-team-page__user-item" v-for="teamUser of sortTeam" :key="teamUser.Id">
          <div class="namespace-team-page__user-item-left">
            <TirPmIconView
              :size="48"
              isRounded
              :isBordered="false"
              :colorCode="teamUser.AvatarColor"
              :imageLoadUrl="teamUser.Photo"
            >
              <template #mainSlot>
                {{ teamUser?.FirstName?.[0].toUpperCase()
                }}{{ teamUser?.LastName?.[0].toUpperCase() }}
              </template>
            </TirPmIconView>
            <div class="namespace-team-page__sign-name">
              {{ teamUser?.FirstName }} {{ teamUser?.MiddleName }} {{ teamUser?.LastName }}
            </div>
          </div>
          <div class="namespace-team-page__user-item-right">
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              @click="(isVisible = true), (teamUserItem = teamUser)"
              v-if="isOwner || isAdminProject"
            >
              <template #leftSlot>
                <TrashIcon />
              </template>
            </TirPmButton>
          </div>
        </div>
        <div v-if="sortTeam.length == 0" style="margin-top: 5rem">
          <TirPmSystemMessage>
            <template #topDescImage>
              <div>
                <TirPmIconView style="width: 5rem; height: 5rem">
                  <template #mainSlot>
                    <UserIcon
                      style="width: 3.5rem; height: 3.5rem; color: var(--neutral-70, #686c73)"
                    />
                  </template>
                </TirPmIconView>
              </div>
            </template>
            <template #mainTitleSlot> Добавьте сотрудников в команду рабочей области </template>
            <template #actionsSlot> </template>
          </TirPmSystemMessage>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, inject, ComputedRef } from "vue";
import { NamespaceQueuesAdapterService } from "./adapter";
import { SaveTeamModel, UserModel } from "./models";
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmModal,
} from "tir-components";
import { TirPmSearchInput, TirPmSearchInputContentLine } from "tir-pm-search-input";
import { CurrentUser, UserRoleEnum } from "tir-pm-user-core";
import { ListBulletIcon, TrashIcon, UserIcon } from "tir-style-system/icons/outline";
import TirPmSystemMessage from "tir-pm-system-message/src/tir-pm-system-message.vue";

const props = defineProps({
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true
  }
});

const adapter = new NamespaceQueuesAdapterService();
/**Владелец */
const isOwner = computed(() => !!CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isAdminProject = inject("isAdminProject") as ComputedRef<boolean>;
/**Админ неймспейса */
const isAdminNamespace = inject("isAdminNamespace") as ComputedRef<boolean>;

/**Загузка */
const isLoading = ref(false);
/**Список идентификаторов сохраненных в команде пользователей в виде строки */
const lastTeamIdsStr = ref("");
/**Список идентификаторов команды */
const teamIds = ref(new Array<number>());
/**Список команды */
const getTeam = async () => {
  const resp = await adapter.getTeam(props.namespaceId);
  if (resp.IsSuccess) {
    teamIds.value = resp.Value.map((user) => user.Id);
    lastTeamIdsStr.value = JSON.stringify(teamIds.value);
  }
};

const teamUserItem = ref();

const searchQueryUsers = ref<string>(null);

/**Сохранить модалку редактирования */
const saveModalEditEmployee = async () => {
  const resp = await adapter.saveTeam(
    new SaveTeamModel({
      NamespaceId: props.namespaceId,
      UserIds: selectedTeamUsers.value.map((x) => x.Id),
      RemoveQueueTasksAccess: true,
      RemoveTaskObserver: true,
      RemoveTaskPerformer: true,
    }),
  );

  if (resp.IsSuccess) {
    await getTeam();
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: "Успешное редактирование",
        type: "success",
        group: "general",
        life: 5000,
        closable: true,
        callbackButton: () => {},
      }),
    );
  } else {
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: resp.ErrorMessage ?? "Произошла ошибка",
        type: "error",
        group: "general",
        life: 5000,
        closable: true,
        callbackButton: () => {},
      }),
    );
  }
};

const copiedEditData = ref();
const isVisible = ref(false);

// Переменная для списка пользователей, чтобы назначить из него админом или участником для селекта
const UserList = ref<Array<UserModel>>([]);

const selectedTeamUsers = ref<Array<UserModel>>([]);
const copiComparedUserList = ref<Array<UserModel>>([]);

/**Сортировка команду юзеров по имени */
const sortTeam = computed(() =>
  selectedTeamUsers.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName)),
);

// Функция для фильтрации юзеров
const filteredUser = (selectedUserName?: UserModel & string) => {
  copiComparedUserList.value = UserList.value;
  copiComparedUserList.value = copiComparedUserList.value.filter(
    (x) => !selectedTeamUsers.value.find((y) => y.Id == x.Id),
  );
  copiComparedUserList.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName));
  if (typeof selectedUserName == "string" && selectedUserName.length) {
    copiComparedUserList.value = copiComparedUserList.value.filter((x) => {
      let compairedString = x?.FirstName + " " + x?.MiddleName + " " + x?.LastName;
      return compairedString.toLowerCase().includes(selectedUserName.toLowerCase());
    });
  }
};

const onMouseDown = (user: UserModel, event: MouseEvent) => {
  if(user.IsProjectAdmin || user.IsNamespaceAdmin) event.preventDefault();
}

// Функция для выбора юзера для селекта команда
const setTeamToNamespace = (selectedUser: UserModel) => {
  if(selectedUser.IsProjectAdmin || selectedUser.IsNamespaceAdmin) return;
  
  selectedTeamUsers.value.push(selectedUser);

  filteredUser();
  saveModalEditEmployee();
  searchQueryUsers.value = "";
};

// Функция для удаления юзера для селекта команда
const removeTeamForNamespace = (removedAdmin: UserModel) => {
  selectedTeamUsers.value = selectedTeamUsers.value.filter((x) => x.Id !== removedAdmin.Id);
  filteredUser();
  saveModalEditEmployee();

  isVisible.value = false;
};

const loadProjects = async () => {
  let resp = await adapter.getEmployees(props.projectId, props.namespaceId);

  UserList.value = resp.Value.map(
    (x) =>
      new UserModel({
        Id: x.Id,
        FirstName: x.FirstName,
        LastName: x.LastName,
        MiddleName: x.MiddleName,
        Photo: x.Photo,
        AvatarColor: x.AvatarColor,
        IsNamespaceAdmin: x.IsNamespaceAdmin,
        IsProjectAdmin: x.IsProjectAdmin
      }),
  );
  copiComparedUserList.value = UserList.value;
  filteredUser();
};

onMounted(async () => {
  try {
    copiedEditData.value = (await adapter.getTeam(props.namespaceId)).Value;
    selectedTeamUsers.value = copiedEditData.value;
    await loadProjects();
  } catch {}
});
</script>

<style lang="scss" scoped>
@use "sass:map";
@import "tir-style-system/expose-variables";
@import "tir-style-system/expose-default-theme";
@import "tir-style-system/core/mixins";

.namespace-team-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  &__title {
    display: block;
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.25rem;
    margin-top: 0;
    margin-bottom: 1rem;
  }
  &__button {
    margin-top: 3rem;
  }
  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &__controls {
    margin-top: 3rem;
  }

  &__user-item {
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: background-color 0.2s ease-in;
    border-radius: 0.5rem;
  }

  &__user-item:hover {
    background-color: var(--neutral-translucent-5, map.get($colors, "neutral-translucent-5"));
  }

  &__user-item-left {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__sign-name {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--text-primary, map.get($colors, "text-primary"));
  }

  &__user-item-right {
    visibility: hidden;
  }

  &__user-item:hover &__user-item-right {
    visibility: visible;
  }
}
</style>
